import { c as _c } from "react/compiler-runtime";
import type { OnboardingSessionConfig } from '@onefootprint/request-types';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { LAYOUT_CONTAINER_ID } from '../../constants';
import FootprintFooter from '../footprint-footer';
import { useLayoutOptions } from '../layout-options-provider';
import NavigationHeaderContainer from '../navigation-header/components/navigation-header-container';
import type { SandboxBannerHandler } from '../sandbox-banner';
import SandboxBanner from '../sandbox-banner';
import { BOTTOM_ACTION_BOX_PORTAL_ID } from '../sticky-bottom-box/constants';
export const IDV_BODY_CONTENT_CONTAINER_ID = 'idv-body-content-container';
type ContentProps = {
  children: React.ReactNode;
  isSandbox?: boolean;
  isHosted?: boolean;
  config?: OnboardingSessionConfig;
  fitContent?: boolean;
};
const Content = t0 => {
  const $ = _c(38);
  const {
    children,
    isSandbox,
    isHosted,
    config,
    fitContent: t1
  } = t0;
  const fitContent = t1 === undefined ? false : t1;
  const {
    options,
    footer
  } = useLayoutOptions();
  let t2;
  if ($[0] !== options) {
    t2 = options || {};
    $[0] = options;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    hideDesktopSandboxBanner,
    hideDesktopFooter
  } = t2;
  const [sandboxBannerHeight, setSandboxBannerHeight] = useState(0);
  const {
    options: t3
  } = footer;
  const {
    height: footerHeight
  } = t3;
  const [bodyContentOverflowing, setBodyContentOverflowing] = useState(false);
  const showSandboxBanner = isSandbox && (config?.sandboxConfig?.showSandboxBanner || isHosted);
  const showFooter = !config?.flags?.hideFootprintFooter || isHosted;
  let t4;
  let t5;
  if ($[2] !== fitContent) {
    t4 = () => {
      if (fitContent) {
        setBodyContentOverflowing(false);
        return;
      }
      const checkOverflow = () => {
        const layoutContainer = document.getElementById(LAYOUT_CONTAINER_ID);
        if (!layoutContainer) {
          return;
        }
        const isOverflowing = layoutContainer.scrollHeight > layoutContainer.clientHeight;
        setBodyContentOverflowing(isOverflowing);
      };
      const resizeObserver = new ResizeObserver(checkOverflow);
      const body = document.querySelector(`#${IDV_BODY_CONTENT_CONTAINER_ID}`);
      if (body) {
        resizeObserver.observe(body);
      }
      return () => {
        if (body) {
          resizeObserver.unobserve(body);
        }
      };
    };
    t5 = [fitContent];
    $[2] = fitContent;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = handler => {
      if (!handler) {
        return;
      }
      const height = handler.getHeight();
      if (height) {
        setSandboxBannerHeight(height);
      }
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const measuredRef = t6;
  const t7 = fitContent ? "grow" : "h-full";
  let t8;
  if ($[6] !== t7) {
    t8 = cx("flex flex-col relative", t7);
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== hideDesktopSandboxBanner || $[9] !== showSandboxBanner) {
    t9 = showSandboxBanner && <SandboxBanner ref={measuredRef} hideOnDesktop={hideDesktopSandboxBanner} />;
    $[8] = hideDesktopSandboxBanner;
    $[9] = showSandboxBanner;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== sandboxBannerHeight) {
    t10 = <NavigationHeaderContainer top={sandboxBannerHeight} containerId={LAYOUT_CONTAINER_ID} />;
    $[11] = sandboxBannerHeight;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t9) {
    t11 = <div className="sticky top-0 z-sticky flex flex-col">{t9}{t10}</div>;
    $[13] = t10;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = fitContent ? "overflow-y-visible" : "overflow-y-auto";
  let t13;
  if ($[16] !== t12) {
    t13 = cx("flex flex-col box-content p-4 h-full overflow-x-hidden md:px-6 md:pb-6 md:pt-0", t12);
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== children || $[19] !== t13) {
    t14 = <div className="flex flex-col relative flex-grow" id={IDV_BODY_CONTENT_CONTAINER_ID}><div className={t13}>{children}</div></div>;
    $[18] = children;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const t15 = bodyContentOverflowing && "border-t border-solid border-tertiary [&:empty]:border-none";
  let t16;
  if ($[21] !== t15) {
    t16 = cx("flex flex-col sticky z-[5]", t15);
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const t17 = `${footerHeight}px`;
  let t18;
  if ($[23] !== t17) {
    t18 = {
      bottom: t17
    };
    $[23] = t17;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t16 || $[26] !== t18) {
    t19 = <div id={BOTTOM_ACTION_BOX_PORTAL_ID} className={t16} style={t18} />;
    $[25] = t16;
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== config || $[29] !== hideDesktopFooter || $[30] !== showFooter) {
    t20 = showFooter && <FootprintFooter hideOnDesktop={hideDesktopFooter} config={config} />;
    $[28] = config;
    $[29] = hideDesktopFooter;
    $[30] = showFooter;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== t11 || $[33] !== t14 || $[34] !== t19 || $[35] !== t20 || $[36] !== t8) {
    t21 = <div className={t8}>{t11}{t14}{t19}{t20}</div>;
    $[32] = t11;
    $[33] = t14;
    $[34] = t19;
    $[35] = t20;
    $[36] = t8;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  return t21;
};
export default Content;